import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import { statusLabels, absenceTypeLabels } from "../../lib/constants";

// ==================== ABSENCES PAGE ====================
const AbsencesPage = () => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  const fetchAbsences = async () => {
    try {
      const response = await api.get('/portal/absences');
      setAbsences(response.data);
    } catch (error) {
      console.error('Error fetching absences:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAbsences(); }, []);

  const handleReview = async (absenceId, accepted) => {
    try {
      await api.put(`/portal/absences/${absenceId}/review`, { accepted });
      toast.success(accepted ? 'Falta justificada' : 'Falta marcada como injustificada');
      fetchAbsences();
    } catch (error) {
      toast.error('Erro ao processar análise');
    }
  };

  return (
    <div data-testid="absences-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Comunicação de Faltas</h1>
          <p className="text-muted-foreground">Comunicar e justificar ausências</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 flex items-center gap-2" data-testid="create-absence-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Comunicar Falta
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : absences.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem faltas registadas</p></div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead><tr><th>Colaborador</th><th>Data</th><th>Tipo</th><th>Estado</th><th>Ações</th></tr></thead>
            <tbody>
              {absences.map((abs) => (
                <tr key={abs.absence_id}>
                  <td className="font-medium">{abs.employee_name}</td>
                  <td>{abs.date}{abs.end_date && abs.end_date !== abs.date ? ` a ${abs.end_date}` : ''}</td>
                  <td>{absenceTypeLabels[abs.absence_type] || abs.absence_type}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      abs.status === 'justificada' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                      abs.status === 'injustificada' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                      'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
                    }`}>{statusLabels[abs.status] || abs.status}</span>
                  </td>
                  <td>
                    {isAdmin && abs.status === 'em_analise' && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleReview(abs.absence_id, true)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Aceitar justificação">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </button>
                        <button onClick={() => handleReview(abs.absence_id, false)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" title="Rejeitar">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && <CreateAbsenceModal onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); fetchAbsences(); }} />}
    </div>
  );
};

const CreateAbsenceModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ date: '', end_date: '', absence_type: 'doenca', justification: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/portal/absences', formData);
      toast.success('Falta comunicada com sucesso');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao comunicar falta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Comunicar Falta</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Data *</label><input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Data Fim</label><input type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Falta *</label>
            <select value={formData.absence_type} onChange={e => setFormData({...formData, absence_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="doenca">Doença</option>
              <option value="acidente">Acidente de Trabalho</option>
              <option value="familiar">Assistência Familiar</option>
              <option value="pessoal">Motivo Pessoal</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Justificação</label>
            <textarea value={formData.justification} onChange={e => setFormData({...formData, justification: e.target.value})} rows={3} className="w-full px-3 py-2 rounded-lg border border-input bg-background resize-none" placeholder="Descreva o motivo da falta..." />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A enviar...' : 'Comunicar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AbsencesPage;
